import { useEffect, useRef, useState } from "react";
import Chart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";

import { ChartTokens } from "./useChartTokens";

/** Room one axis label needs — "11 ago", "sept 26" — with air around it. */
const LABEL_WIDTH = 64;

/** The chart's own width, so the axis can decide how many labels fit. */
function useWidth() {
    const ref = useRef<HTMLDivElement>(null);
    const [width, setWidth] = useState(0);

    useEffect(() => {
        const element = ref.current;
        if (!element || typeof ResizeObserver === "undefined") return;
        const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
        observer.observe(element);
        return () => observer.disconnect();
    }, []);

    return { ref, width };
}

export interface TimeSeries {
    name: string;
    data: number[];
    color: string;
}

interface Props {
    kind: "bar" | "line";
    /** Short labels for the axis, one per period. */
    categories: string[];
    /** The whole period, for the tooltip's title. */
    fullLabels: string[];
    series: TimeSeries[];
    tokens: ChartTokens;
    /** Stack the bars — for parts of one whole, like paid and pending. */
    stacked?: boolean;
    formatValue: (value: number) => string;
    /** A shorter form for the value axis; defaults to `formatValue`. */
    formatAxis?: (value: number) => string;
    /** What the chart shows, for assistive technology. */
    ariaLabel: string;
    height?: number;
}

/**
 * A measure over time, on one axis.
 *
 * Bars for counts per period, lines for two measures that are read against
 * each other. Two or more series get a legend, and a line also labels its last
 * point, so no series is known by its colour alone.
 */
export default function TimeSeriesChart({
    kind,
    categories,
    fullLabels,
    series,
    tokens,
    stacked = false,
    formatValue,
    formatAxis = formatValue,
    ariaLabel,
    height = 260,
}: Props) {
    const { ref, width } = useWidth();
    const many = categories.length > 14;
    const last = categories.length - 1;

    // Label every nth period, as many as the width holds. Apex's own overlap
    // hiding does not work on a category axis, and a phone gets a pile-up.
    // The tooltip still names every period in full.
    const fits = Math.max(2, Math.floor((width || 640) / LABEL_WIDTH));
    const step = Math.ceil(categories.length / fits);
    const axisLabels = categories.map((label, index) => (index % step === 0 ? label : ""));

    // The surface gap is 2px, until a bar is so thin the gap would eat it: a
    // month by day on a phone leaves each period about ten pixels.
    const slot = (width || 640) / Math.max(1, categories.length);
    const gap = slot < 14 ? (stacked ? 1 : 0) : 2;

    const options: ApexOptions = {
        chart: {
            type: kind,
            stacked,
            fontFamily: "inherit",
            toolbar: { show: false },
            zoom: { enabled: false },
            animations: { enabled: true, speed: 400 },
        },
        colors: series.map((s) => s.color),
        // Apex fades bars to 0.85 by default; the legend swatch is solid.
        fill: { opacity: 1 },
        plotOptions: {
            bar: {
                borderRadius: 4,
                borderRadiusApplication: "end",
                borderRadiusWhenStacked: "last",
                columnWidth: slot < 14 ? "85%" : many ? "70%" : "45%",
            },
        },
        // Bars: a gap of surface between neighbours and between stacked parts.
        // Lines: the 2px stroke is the mark.
        stroke:
            kind === "bar"
                ? { show: gap > 0, width: gap, colors: [tokens.surface] }
                : { show: true, width: 2, curve: "straight" },
        // Apex reads `markers.size` for every chart type, so bars need the
        // object too — an absent one throws on render.
        markers:
            kind === "line"
                ? {
                      size: many ? 0 : 4,
                      strokeWidth: 2,
                      strokeColors: tokens.surface,
                      hover: { size: 5 },
                  }
                : { size: 0 },
        dataLabels:
            kind === "line" && series.length > 1
                ? {
                      enabled: true,
                      formatter: (value, { dataPointIndex }) =>
                          dataPointIndex === last ? formatValue(Number(value)) : "",
                      offsetY: -6,
                      background: { enabled: false },
                      style: { fontSize: "12px", fontWeight: 500, colors: [tokens.ink] },
                  }
                : { enabled: false },
        legend: {
            show: series.length > 1,
            position: "top",
            horizontalAlign: "left",
            fontSize: "12px",
            markers: { size: 5, offsetX: -2 },
            itemMargin: { horizontal: 8 },
        },
        grid: {
            strokeDashArray: 0,
            xaxis: { lines: { show: false } },
            yaxis: { lines: { show: true } },
            padding: { left: 12, right: 12 },
        },
        xaxis: {
            categories: axisLabels,
            axisBorder: { show: false },
            axisTicks: { show: false },
            labels: { rotate: 0, trim: false },
            tooltip: { enabled: false },
        },
        yaxis: {
            min: 0,
            forceNiceScale: true,
            labels: { formatter: (value) => formatAxis(value) },
        },
        tooltip: {
            shared: true,
            intersect: false,
            theme: "light",
            x: { formatter: (_value, opts) => fullLabels[opts?.dataPointIndex ?? 0] ?? "" },
            y: { formatter: (value) => formatValue(value) },
        },
        states: { hover: { filter: { type: "darken" } } },
    };

    return (
        <div ref={ref} role="img" aria-label={ariaLabel} className="-mx-2">
            <Chart
                options={options}
                series={series.map(({ name, data }) => ({ name, data }))}
                type={kind}
                height={height}
                width="100%"
            />
        </div>
    );
}
